import { useSelector } from 'react-redux';
import { Container, Group, GridCol, Grid, Button } from '@mantine/core';
import { fetchEquipmentPeriodReport, PeriodTotals } from '../../../../libraries/periods';
import { Language } from '../../../languages/languageHandler';
import { ICurrency } from '../../../../libraries/currencies';
import { AppState } from '../../../client-redux/reducers';
import ModalComponent from '../../../components/modal';
import { formatDateTimeLabel } from '../../../utils/date';
import { formatCurrAmount, getLocationName } from '../../../utils/script';
import PeriodCurrencyTable from '../period-table/period-currency-table';
import DisplayField from '../../../components/display-field';
import * as Icon from '@tabler/icons-react';
import './styles.scss';
import { useState } from 'react';
import CustomModal from '../../../components/custom-modal';
interface ShowPeriodProps {
  period: PeriodTotals;
  openDetails: boolean;
  hideBtn: () => void;
  lang: Language;
  currency?: ICurrency;
  isDeposit?: boolean;
}

const ShowPeriodDetails = ({ period, openDetails, hideBtn, lang, isDeposit }: ShowPeriodProps) => {
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const controller = new AbortController();

  const getPeriodReport = async (format: string, eid: number, period: number) => {
    if (!format || !eid || !period) return;

    try {
      await fetchEquipmentPeriodReport(format, eid, period, controller);
    } catch (err: any) {
      const message = err.statusOper ? `Code: ${err.statusOper.code} - ${err.statusOper.message}` : err.message;

      setErrorMessage(`${lang.PERIODS_REPORT_ERROR}: ${message}`);
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
    }
  };
  return (
    <>
      <ModalComponent
        show={openDetails}
        handleClick={hideBtn}
        title={`${lang.ENTERPRISE_PERIOD} ${period.eid}-${period.period}`}
        content={
          <>
            <Container classNames={{ root: 'period-details' }}>
              <Group>
                <Grid gutter="sm">
                  {/* Local */}
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.EQUIPMENT_LOCAL} content={getLocationName(period.sid)} />
                  </GridCol>

                  {/* Eid */}
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.EQUIPMENT_ID} content={period.eid} />
                  </GridCol>

                  {/* Period */}
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.GLOBAL_PERIOD} content={period.period} />
                  </GridCol>

                  {/* Code */}
                  {period.code && (
                    <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                      <DisplayField title={lang.ENTERPRISE_EQUIP_PERIODS_PAYMENT_CLOSE_CODE} content={period.code} />
                    </GridCol>
                  )}

                  {/* Open Date */}
                  <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                    <DisplayField title={lang.GLOBAL_DATE} content={formatDateTimeLabel(period.dateTimeOpen)} />
                  </GridCol>

                  {/* Open User */}
                  {period.userOpen && (
                    <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                      <DisplayField
                        title={lang.ENTERPRISE_EQUIP_PERIODS_USER_OPEN}
                        content={period.userOpen ? `${period.userOpen?.uid} - ${period.userOpen?.name}` : '-'}
                      />
                    </GridCol>
                  )}

                  {/* Close Date */}
                  {period.dateTimeClose && (
                    <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                      <DisplayField
                        title={lang.ENTERPRISE_EQUIP_PERIODS_DT_CLOSE}
                        content={formatDateTimeLabel(period.dateTimeClose)}
                      />
                    </GridCol>
                  )}

                  {/* Close User */}
                  {period.userClose && (
                    <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                      <DisplayField
                        title={lang.ENTERPRISE_EQUIP_PERIODS_USER_CLOSE}
                        content={period.userClose ? `${period.userClose?.uid} - ${period.userClose?.name}` : '-'}
                      />
                    </GridCol>
                  )}
                </Grid>

                {!isDeposit &&
                  (period.totals ?? []).length > 0 &&
                  period.totals.map((pa, index) => {
                    const curr = currencies.find(c => c.cid === pa.cid);
                    if (!curr) return null;

                    if (!pa.total || pa.total === 0) return null;

                    return (
                      <>
                        <Grid gutter="sm">
                          <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                            <DisplayField title={lang.PERIOD_TOTAL_AMOUNT} content={formatCurrAmount(pa.total, curr)} />
                          </GridCol>
                          <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                            <DisplayField title={lang.PERIOD_TOTAL_PAID} content={formatCurrAmount(pa.paid, curr)} />
                          </GridCol>
                          <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                            <DisplayField
                              title={lang.PERIOD_TOTAL_PAID_CASH}
                              content={formatCurrAmount(pa.paid - pa.paidAP, curr)}
                            />
                          </GridCol>
                          <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                            <DisplayField
                              title={lang.PERIOD_TOTAL_PAID_CARD}
                              content={formatCurrAmount(pa.paidAP, curr)}
                            />
                          </GridCol>
                        </Grid>
                        <PeriodCurrencyTable
                          key={index}
                          lang={lang}
                          currency={curr}
                          cash={period.mc ? period.mc.filter(m => m.cid === pa.cid) : []}
                        />
                      </>
                    );
                  })}

                {isDeposit &&
                  (period.totals ?? []).length > 0 &&
                  period.totals.map((pa, index) => {
                    const curr = currencies.find(c => c.cid === pa.cid);
                    if (!curr) return null;

                    if (!pa.total || pa.total === 0) return null;

                    return (
                      <PeriodCurrencyTable
                        key={index}
                        lang={lang}
                        label={`${lang.GLOBAL_AMOUNT}: ${formatCurrAmount(pa.total, curr)}`}
                        currency={curr}
                        cash={period.mc ? period.mc.filter(m => m.cid === pa.cid) : []}
                      />
                    );
                  })}
              </Group>

              <Group className="buttons">
                {period.useReport && (
                  <Button radius="md" onClick={() => getPeriodReport('pdf', period.eid, period.period)}>
                    <Icon.IconFileTypePdf stroke={2} />
                    PDF
                  </Button>
                )}
                {period.dateTimeClose && (
                  <Button radius="md" onClick={() => getPeriodReport('xls', period.eid, period.period)}>
                    <Icon.IconFileTypeXls stroke={2} />
                    XLS
                  </Button>
                )}
              </Group>
            </Container>
          </>
        }
      />
      <CustomModal
        opened={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={errorMessage}
        backgroundColor="#E3353F"
        textColor="white"
      />
    </>
  );
};

export default ShowPeriodDetails;
